import { css } from '@emotion/css';
import { Box } from '@spark-web/box';
import { InformationCircleIcon } from '@spark-web/icon';
import { Text } from '@spark-web/text';

function CustomToolTip({ id, text }: { id: string; text: string }) {
  return (
    <span className={tooltipTrigger} tabIndex={0} aria-label="More information" aria-describedby={id}>
      <InformationCircleIcon size="small" tone="info" />
      <Box
        id={id}
        role="tooltip"
        className={`custom-tooltip-bubble ${tooltipBubble}`}
        background="surface"
        border="neutral"
        borderRadius="medium"
        padding="large"
        shadow="medium"
      >
        <Text tone="muted">{text}</Text>
      </Box>
    </span>
  );
}

const tooltipTrigger = css({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'help',
  '&:hover .custom-tooltip-bubble, &:focus .custom-tooltip-bubble, &:focus-visible .custom-tooltip-bubble': {
    opacity: 1,
    visibility: 'visible',
  },
});

// The Box owns the surface (background, border, shadow, padding) via theme
// tokens; this class only handles positioning and the hover/focus reveal.
const tooltipBubble = css({
  position: 'absolute',
  // Mobile-first: anchor above the icon and align to its right edge, with the
  // width clamped to the viewport so the bubble never overflows the form or
  // adds horizontal scroll on small screens.
  bottom: 'calc(100% + 8px)',
  right: 0,
  width: 'max-content',
  maxWidth: 'min(280px, calc(100vw - 48px))',
  textAlign: 'left',
  whiteSpace: 'normal',
  opacity: 0,
  visibility: 'hidden',
  // Fast fade with no delay, so it appears almost immediately on hover/focus.
  transition: 'opacity 80ms ease-out, visibility 80ms ease-out',
  zIndex: 20,
  pointerEvents: 'none',
  // Only place the bubble to the right when there is room beside it: on desktop,
  // or on a tablet held in landscape (portrait tablets and mobile keep it above).
  '@media (min-width: 1080px)': {
    top: '50%',
    right: 'auto',
    bottom: 'auto',
    left: 'calc(100% + 8px)',
    transform: 'translateY(-50%)',
    maxWidth: 280,
  },
});

export default CustomToolTip;
